import { useState } from 'react';
import { ImageOff, Search } from 'lucide-react';

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import InputClearButton from '@/components/input-clear-button';
import InputSpinner from '@/components/input-spinner';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemHeader,
  ItemTitle,
} from '@/components/ui/item';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDebounce } from '@/lib/hooks';
import type { Ratio } from '@/models/zoneplacement';
import { useLibraryCreatives, type LibraryCreative } from './hooks';

interface Props {
  ratio: Ratio | undefined;
  setAsset: (asset: LibraryCreative) => void;
}

export function AssetLibrary({ ratio, setAsset }: Props) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const { data: assets, isFetching } = useLibraryCreatives(
    ratio,
    debouncedSearch,
  );

  return (
    <>
      <div className="relative shrink-0">
        <Search className="text-muted-foreground absolute top-2.5 left-2 size-4" />
        <Input
          className="rounded-t-xl rounded-b-none pl-8"
          name="search"
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search assets"
          value={search}
        />
        {!isFetching && search && (
          <InputClearButton onClick={() => setSearch('')} />
        )}
        {isFetching && <InputSpinner />}
      </div>
      <div className="h-91 overflow-hidden rounded-xl rounded-t-none border border-t-0">
        <div className="h-full overflow-y-scroll">
          {assets && assets.length === 0 && (
            <Empty className="mx-auto mt-4 w-fit border border-dashed">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Search />
                </EmptyMedia>
                <EmptyTitle>No results found</EmptyTitle>
                <EmptyDescription>Modify your search query</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          {assets && assets.length > 0 && (
            <ItemGroup className="grid min-h-0 flex-1 grid-cols-2 items-start overflow-y-auto p-4 md:grid-cols-3 lg:grid-cols-4">
              {assets.map((a) => {
                const thumbnail = a.assets.find(
                  (a) => a.type === 'thumbnail',
                )?.url;

                return (
                  <Item key={a.id} asChild role="listitem" variant="outline">
                    <button
                      className="group"
                      onClick={() => setAsset(a)}
                      type="button"
                    >
                      <ItemHeader className="relative">
                        {thumbnail ? (
                          <img
                            alt={a.name}
                            className="h-24 w-full rounded object-cover"
                            src={thumbnail}
                          />
                        ) : (
                          <div className="bg-muted flex h-24 w-full items-center justify-center rounded">
                            <ImageOff className="size-5" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center rounded bg-white/50 opacity-0 backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100">
                          <span className="text-sm font-medium">Select</span>
                        </div>
                      </ItemHeader>
                      <ItemContent>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <ItemTitle>{a.name}</ItemTitle>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md wrap-break-word">
                            {a.name}
                          </TooltipContent>
                        </Tooltip>
                        <ItemDescription>
                          {a.duration_ms / 1_000}s
                        </ItemDescription>
                      </ItemContent>
                    </button>
                  </Item>
                );
              })}
            </ItemGroup>
          )}
        </div>
      </div>
    </>
  );
}
